import type { VisitResponse, VisitTypeId } from '../services/api/types.gen';
import type { DisciplineType } from '../services/api/models/DisciplineType';
import type { OrderTypeId } from '../services/api/models/OrderTypeId';

/** Either a visit or an order task type. */
export type TaskTypeId = VisitTypeId | OrderTypeId;

let visitTypeLegacyById: Record<string, boolean> | null = null;

/** Populated when ``/visits/type-options`` is fetched (see useVisitTypeOptions). */
export function cacheVisitTypeLegacyFlags(
  options: ReadonlyArray<{ id: string; is_legacy: boolean }>
): void {
  visitTypeLegacyById = Object.fromEntries(
    options.map(option => [option.id, option.is_legacy])
  );
}

export const TASK_TYPE_DISCIPLINE_MAP: Record<TaskTypeId, DisciplineType> = {
  OASIS_E2_START_OF_CARE: 'SN',
  OASIS_E2_START_OF_CARE_ENHANCED: 'SN',
  OASIS_E2_RESUMPTION_OF_CARE: 'SN',
  OASIS_E2_RESUMPTION_OF_CARE_ENHANCED: 'SN',
  OASIS_E2_RECERTIFICATION: 'SN',
  OASIS_E2_RECERTIFICATION_ENHANCED: 'SN',
  OASIS_E2_DISCHARGE: 'SN',
  OASIS_E2_DISCHARGE_ENHANCED: 'SN',
  OASIS_E2_DISCHARGE_NON_BILLABLE: 'SN',
  OASIS_E2_START_OF_CARE_PT: 'PT',
  OASIS_E2_START_OF_CARE_PT_ENHANCED: 'PT',
  OASIS_E2_RESUMPTION_OF_CARE_PT: 'PT',
  OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED: 'PT',
  OASIS_E2_RECERTIFICATION_PT: 'PT',
  OASIS_E2_RECERTIFICATION_PT_ENHANCED: 'PT',
  OASIS_E2_DISCHARGE_PT: 'PT',
  OASIS_E2_DISCHARGE_PT_ENHANCED: 'PT',
  OASIS_E2_DISCHARGE_PT_NON_BILLABLE: 'PT',
  OASIS_E2_START_OF_CARE_OT: 'OT',
  OASIS_E2_START_OF_CARE_OT_ENHANCED: 'OT',
  OASIS_E2_RESUMPTION_OF_CARE_OT: 'OT',
  OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED: 'OT',
  OASIS_E2_RECERTIFICATION_OT: 'OT',
  OASIS_E2_RECERTIFICATION_OT_ENHANCED: 'OT',
  OASIS_E2_DISCHARGE_OT: 'OT',
  OASIS_E2_DISCHARGE_OT_ENHANCED: 'OT',
  OASIS_E2_DISCHARGE_OT_NON_BILLABLE: 'OT',
  OASIS_E2_START_OF_CARE_ST: 'ST',
  OASIS_E2_START_OF_CARE_ST_ENHANCED: 'ST',
  OASIS_E2_RESUMPTION_OF_CARE_ST: 'ST',
  OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED: 'ST',
  OASIS_E2_RECERTIFICATION_ST: 'ST',
  OASIS_E2_RECERTIFICATION_ST_ENHANCED: 'ST',
  OASIS_E2_DISCHARGE_ST: 'ST',
  OASIS_E2_DISCHARGE_ST_ENHANCED: 'ST',
  OASIS_E2_DISCHARGE_ST_NON_BILLABLE: 'ST',
  OASIS_E2_FOLLOW_UP: 'SN',
  OASIS_E2_FOLLOW_UP_ENHANCED: 'SN',
  OASIS_E2_TRANSFER: 'SN',
  OASIS_E2_TRANSFER_ENHANCED: 'SN',
  OASIS_E2_DEATH: 'SN',
  OASIS_E2_DEATH_ENHANCED: 'SN',
  OASIS_E2_FOLLOW_UP_PT: 'PT',
  OASIS_E2_FOLLOW_UP_PT_ENHANCED: 'PT',
  OASIS_E2_TRANSFER_PT: 'PT',
  OASIS_E2_TRANSFER_PT_ENHANCED: 'PT',
  OASIS_E2_DEATH_PT: 'PT',
  OASIS_E2_DEATH_PT_ENHANCED: 'PT',
  OASIS_E2_FOLLOW_UP_OT: 'OT',
  OASIS_E2_FOLLOW_UP_OT_ENHANCED: 'OT',
  OASIS_E2_TRANSFER_OT: 'OT',
  OASIS_E2_TRANSFER_OT_ENHANCED: 'OT',
  OASIS_E2_DEATH_OT: 'OT',
  OASIS_E2_DEATH_OT_ENHANCED: 'OT',
  OASIS_E2_FOLLOW_UP_ST: 'ST',
  OASIS_E2_FOLLOW_UP_ST_ENHANCED: 'ST',
  OASIS_E2_TRANSFER_ST: 'ST',
  OASIS_E2_TRANSFER_ST_ENHANCED: 'ST',
  OASIS_E2_DEATH_ST: 'ST',
  OASIS_E2_DEATH_ST_ENHANCED: 'ST',
  SKILLED_NURSING_ASSESSMENT: 'SN',
  SKILLED_NURSING_EVALUATION: 'SN',
  LPN_LVN_SKILLED_NURSING_VISIT: 'SN',
  RN_SKILLED_NURSING_VISIT: 'SN',
  // Additional SNV aliases (all RN note form, SN discipline)
  SKILLED_NURSING_VISIT: 'SN',
  PRN_SKILLED_NURSING_VISIT: 'SN',
  WOUND_CARE_VISIT: 'SN',
  INFUSION_THERAPY_VISIT: 'SN',
  CATHETER_CARE_VISIT: 'SN',
  OSTOMY_CARE_VISIT: 'SN',
  DIABETIC_MANAGEMENT_VISIT: 'SN',
  MEDICATION_MANAGEMENT_VISIT: 'SN',
  INJECTION_VISIT: 'SN',
  LPN_LVN_WOUND_CARE_VISIT: 'SN',
  PICC_LABS: 'SN',
  PICC_VISIT: 'SN',
  SN_LABS: 'SN',
  FOLEY_CATHETER_CHANGE: 'SN',
  LVN_SUPERVISORY_VISIT: 'SN',
  PTA_SUPERVISORY_VISIT: 'PT',
  COTA_SUPERVISORY_VISIT: 'OT',
  HHA_SUPERVISORY_VISIT: 'HHA',
  AIDE_SUPERVISORY_VISIT: 'HHA',
  CASE_CONFERENCE_60_DAY_SUMMARY: 'SN',
  SN_DISCHARGE_SUMMARY: 'SN',
  PT_DISCHARGE_SUMMARY: 'PT',
  OT_DISCHARGE_SUMMARY: 'OT',
  ST_DISCHARGE_SUMMARY: 'ST',
  PT_EVALUATION: 'PT',
  PT_RE_EVALUATION: 'PT',
  PT_RE_EVALUATION_WITH_SUPERVISORY_VISIT: 'PT',
  PT_VISIT: 'PT',
  PT_TREATMENT_VISIT: 'PT',
  PTA_VISIT: 'PT',
  PT_DISCHARGE: 'PT',
  PT_DISCHARGE_VISIT: 'PT',
  OT_EVALUATION: 'OT',
  OT_RE_EVALUATION: 'OT',
  OT_VISIT: 'OT',
  OT_TREATMENT_VISIT: 'OT',
  COTA_VISIT: 'OT',
  OT_DISCHARGE: 'OT',
  OT_DISCHARGE_VISIT: 'OT',
  ST_EVALUATION: 'ST',
  ST_RE_EVALUATION: 'ST',
  ST_VISIT: 'ST',
  ST_TREATMENT_VISIT: 'ST',
  ST_DISCHARGE: 'ST',
  ST_DISCHARGE_VISIT: 'ST',
  MSW_EVALUATION: 'MSW',
  MSW_VISIT: 'MSW',
  MSW_DISCHARGE: 'MSW',
  AIDE_CARE_PLAN: 'HHA',
  AIDE_CARE_PLAN_REVISION: 'HHA',
  HHA_VISIT: 'HHA',
  // Order types (live in OrderTypeId after ROG-2320; values mirror
  // ORDER_TYPE_INFO[*].discipline in the backend — all SN today).
  CMS_485: 'SN',
  FREQUENCY_DISCONTINUE_ORDER: 'SN',
  FREQUENCY_ORDER: 'SN',
  PRN_ORDER: 'SN',
  CMS_486: 'SN',
  PHYSICIAN_ORDER: 'SN',
  POST_HOSPITAL_ORDER: 'SN',
  TP_1: 'SN',
  TP_2: 'SN',
  SCIC_ORDER: 'SN',
  F2F_ENCOUNTER: 'SN',
  MEDICAID_POC: 'SN',
  PLAN_OF_CARE_ADDENDUM: 'SN',
  MEDICATION_ORDER: 'SN',
  // Nominal, like the backend's ORDER_TYPE_INFO entry. A signature request
  // carries the source visit's own discipline on the order row itself.
  SIGNABLE_VISIT_ORDER: 'SN',
};

// ── Visit module configuration ───────────────────────────────────────────────
//
// One row per visit type; the columns are the shared clinical modules that
// visit's note carries. Read it as a table, edit it as a table — the predicates
// below are lookups into it and contain no rules of their own.
//
// This mirrors `FORM_TYPE_MODULES` in `backend/app/models/enum/visit.py`, which
// holds the same table keyed by note form (a visit type is an alias for a form,
// so 115 rows here map onto 46 there). The backend is where these rules are
// enforced; this copy is what keeps the UI from offering a section the server
// will reject. Edit both together —
// `backend/tests/models/test_visit_type_capabilities.py` compares the two
// tables cell for cell and fails on any drift.

export type VisitModule =
  | 'ACTIVE_DIAGNOSIS'
  | 'WOUNDS'
  | 'EVENTS'
  /** Can start new goals (evaluations, and the assessments that open a period). */
  | 'GOALS_ADD'
  /** Can record progress against existing goals (routine visits, discharges). */
  | 'GOALS_PROGRESS'
  /** Displays the episode's in-force diagnoses read-only; records none. */
  | 'DIAGNOSIS_REVIEW';

// One row per visit type. Kept unwrapped so it reads as a table -- prettier
// would otherwise split each row across six lines.
// prettier-ignore
export const VISIT_TYPE_MODULES: Record<VisitTypeId, readonly VisitModule[]> = {
  OASIS_E2_START_OF_CARE: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_ENHANCED: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_NON_BILLABLE: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_PT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_PT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_PT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_PT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_PT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_PT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_PT: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_PT_ENHANCED: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_PT_NON_BILLABLE: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_OT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_OT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_OT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_OT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_OT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_OT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_OT: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_OT_ENHANCED: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_OT_NON_BILLABLE: ['WOUNDS', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_ST: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_START_OF_CARE_ST_ENHANCED: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_ST: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RESUMPTION_OF_CARE_ST_ENHANCED: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_ST: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_RECERTIFICATION_ST_ENHANCED: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_ST: ['GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_ST_ENHANCED: ['GOALS_PROGRESS'],
  OASIS_E2_DISCHARGE_ST_NON_BILLABLE: ['GOALS_PROGRESS'],
  OASIS_E2_FOLLOW_UP: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_FOLLOW_UP_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_TRANSFER: ['WOUNDS'],
  OASIS_E2_TRANSFER_ENHANCED: ['WOUNDS'],
  OASIS_E2_DEATH: [],
  OASIS_E2_DEATH_ENHANCED: [],
  OASIS_E2_FOLLOW_UP_PT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_FOLLOW_UP_PT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_TRANSFER_PT: [],
  OASIS_E2_TRANSFER_PT_ENHANCED: [],
  OASIS_E2_DEATH_PT: [],
  OASIS_E2_DEATH_PT_ENHANCED: [],
  OASIS_E2_FOLLOW_UP_OT: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_FOLLOW_UP_OT_ENHANCED: ['ACTIVE_DIAGNOSIS', 'WOUNDS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_TRANSFER_OT: [],
  OASIS_E2_TRANSFER_OT_ENHANCED: [],
  OASIS_E2_DEATH_OT: [],
  OASIS_E2_DEATH_OT_ENHANCED: [],
  OASIS_E2_FOLLOW_UP_ST: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_FOLLOW_UP_ST_ENHANCED: ['ACTIVE_DIAGNOSIS', 'GOALS_ADD', 'GOALS_PROGRESS'],
  OASIS_E2_TRANSFER_ST: [],
  OASIS_E2_TRANSFER_ST_ENHANCED: [],
  OASIS_E2_DEATH_ST: [],
  OASIS_E2_DEATH_ST_ENHANCED: [],
  SKILLED_NURSING_ASSESSMENT: ['WOUNDS', 'GOALS_ADD'],
  SKILLED_NURSING_EVALUATION: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  LPN_LVN_SKILLED_NURSING_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  RN_SKILLED_NURSING_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  SKILLED_NURSING_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PRN_SKILLED_NURSING_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  WOUND_CARE_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  INFUSION_THERAPY_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  CATHETER_CARE_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  OSTOMY_CARE_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  DIABETIC_MANAGEMENT_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  MEDICATION_MANAGEMENT_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  INJECTION_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  LPN_LVN_WOUND_CARE_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PICC_LABS: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PICC_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  SN_LABS: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  FOLEY_CATHETER_CHANGE: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  LVN_SUPERVISORY_VISIT: [],
  PTA_SUPERVISORY_VISIT: [],
  COTA_SUPERVISORY_VISIT: [],
  HHA_SUPERVISORY_VISIT: [],
  AIDE_SUPERVISORY_VISIT: [],
  CASE_CONFERENCE_60_DAY_SUMMARY: ['WOUNDS'],
  SN_DISCHARGE_SUMMARY: ['GOALS_PROGRESS', 'DIAGNOSIS_REVIEW'],
  PT_EVALUATION: ['WOUNDS', 'GOALS_ADD'],
  PT_RE_EVALUATION: ['WOUNDS', 'GOALS_ADD'],
  PT_RE_EVALUATION_WITH_SUPERVISORY_VISIT: ['WOUNDS', 'GOALS_ADD'],
  PT_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PT_TREATMENT_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PTA_VISIT: ['WOUNDS', 'EVENTS', 'GOALS_PROGRESS'],
  PT_DISCHARGE: ['WOUNDS', 'GOALS_PROGRESS'],
  PT_DISCHARGE_VISIT: ['WOUNDS', 'GOALS_PROGRESS'],
  PT_DISCHARGE_SUMMARY: ['GOALS_PROGRESS', 'DIAGNOSIS_REVIEW'],
  OT_EVALUATION: ['GOALS_ADD'],
  OT_RE_EVALUATION: ['GOALS_ADD'],
  OT_VISIT: ['GOALS_PROGRESS'],
  OT_TREATMENT_VISIT: ['GOALS_PROGRESS'],
  COTA_VISIT: ['GOALS_PROGRESS'],
  OT_DISCHARGE: ['GOALS_PROGRESS'],
  OT_DISCHARGE_VISIT: ['GOALS_PROGRESS'],
  OT_DISCHARGE_SUMMARY: ['GOALS_PROGRESS', 'DIAGNOSIS_REVIEW'],
  ST_EVALUATION: ['GOALS_ADD'],
  ST_RE_EVALUATION: ['GOALS_ADD'],
  ST_VISIT: ['GOALS_PROGRESS'],
  ST_TREATMENT_VISIT: ['GOALS_PROGRESS'],
  ST_DISCHARGE: ['GOALS_PROGRESS'],
  ST_DISCHARGE_VISIT: ['GOALS_PROGRESS'],
  ST_DISCHARGE_SUMMARY: ['GOALS_PROGRESS', 'DIAGNOSIS_REVIEW'],
  MSW_EVALUATION: [],
  MSW_VISIT: [],
  MSW_DISCHARGE: [],
  AIDE_CARE_PLAN: [],
  AIDE_CARE_PLAN_REVISION: [],
  HHA_VISIT: [],
};

const hasModule = (visitType: VisitTypeId, module: VisitModule): boolean =>
  VISIT_TYPE_MODULES[visitType]?.includes(module) ?? false;

/** Whether this visit type has a wound section. */
export const supportsWounds = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'WOUNDS');

/** Whether this visit type's note shows the falls/infections Events band. */
export const showsEventsSection = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'EVENTS');

/** Whether this visit type can start new goals. */
export const canAddGoals = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'GOALS_ADD');

/** Whether this visit type can record goal and intervention progress. */
export const canTrackGoalProgress = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'GOALS_PROGRESS');

/**
 * Whether the visit shows a Goals section at all. Most notes do only one of the
 * two: an evaluation starts goals, while a routine visit or a discharge records
 * progress against them. The section needs either capability.
 */
export const showsGoalsSection = (visitType: VisitTypeId): boolean =>
  canAddGoals(visitType) || canTrackGoalProgress(visitType);

/**
 * Whether the note displays the episode's in-force diagnoses read-only.
 *
 * The read-only twin of `supportsActiveDiagnosisPage`, and never both: a note
 * that displays diagnoses does not own them, and must offer no way to add,
 * reorder, or edit a row. Anything it wrote would reach the billing worksheet.
 */
export const showsDiagnosisReview = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'DIAGNOSIS_REVIEW');

/** Whether the visit shows the Active Diagnosis page (excludes DC and TF). */
export const supportsActiveDiagnosisPage = (visitType: VisitTypeId): boolean =>
  hasModule(visitType, 'ACTIVE_DIAGNOSIS');

export const isSNDiscipline = (typeId: TaskTypeId): boolean =>
  TASK_TYPE_DISCIPLINE_MAP[typeId] === 'SN';

/** Order type IDs. Mirrors backend `OrderTypeId` (app/models/enum/order.py). */
export const ORDER_TYPE_IDS: ReadonlySet<OrderTypeId> = new Set<OrderTypeId>([
  'CMS_485',
  'FREQUENCY_DISCONTINUE_ORDER',
  'FREQUENCY_ORDER',
  'PRN_ORDER',
  'CMS_486',
  'PHYSICIAN_ORDER',
  'POST_HOSPITAL_ORDER',
  'TP_1',
  'TP_2',
  'SCIC_ORDER',
  'F2F_ENCOUNTER',
  'MEDICAID_POC',
  'PLAN_OF_CARE_ADDENDUM',
  'MEDICATION_ORDER',
  'SIGNABLE_VISIT_ORDER',
]);

export const isOrderType = (typeId: TaskTypeId): typeId is OrderTypeId =>
  ORDER_TYPE_IDS.has(typeId as OrderTypeId);

/**
 * Order type IDs we keep in the enum for legacy data but hide from creation
 * pickers (Add Task, intake document tagging, etc.). CMS 486 will not be
 * supported (per ROG-2154); SCIC support is tracked separately in
 * ROG-2160 / ROG-2209.
 */
export const HIDDEN_ORDER_TYPE_IDS: ReadonlySet<OrderTypeId> =
  new Set<OrderTypeId>(['CMS_486', 'SCIC_ORDER']);

export const isHiddenOrderType = (typeId: TaskTypeId): boolean =>
  HIDDEN_ORDER_TYPE_IDS.has(typeId as OrderTypeId);

export function isCreatableVisitTypeId(typeId: TaskTypeId): boolean {
  if (isOrderType(typeId)) {
    return !isHiddenOrderType(typeId);
  }
  if (visitTypeLegacyById && typeId in visitTypeLegacyById) {
    return !visitTypeLegacyById[typeId];
  }
  return false;
}

/**
 * Whether a visit offers answer carryover (EMR-675 RN, EMR-1008 PT).
 *
 * Read straight off the visit rather than kept as a local list of form types:
 * the backend computes `supports_answer_carryover` from the same
 * `carryover_form_type` it enforces on the request, so enabling a new form is
 * one backend edit and the button cannot appear on a form the API rejects.
 */
export const supportsAnswerCarryover = (
  visit: Pick<VisitResponse, 'supports_answer_carryover'> | null | undefined
): boolean => visit?.supports_answer_carryover === true;
